import React, { useState } from 'react';
import { Plus, Edit2, Save, X, Trash2, Package } from 'lucide-react';
import Modal from './Modal';
import { categoryColor } from '../utils/categoryColors';
import { useLanguage } from '../context/LanguageContext';

function ExpenseItemsModal({
  isOpen,
  onClose,
  expenseItems = [],
  onAddItem,
  onEditItem,
  onDeleteItem
}) {
  const { t } = useLanguage();
  const [newItemName, setNewItemName] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemName, setEditingItemName] = useState('');

  const handleAddItem = () => {
    if (!newItemName.trim()) return;
    
    onAddItem(newItemName.trim());
    setNewItemName('');
  };

  const handleEditItem = (item) => {
    setEditingItemId(item._id);
    setEditingItemName(item.name);
  };

  const handleSaveEdit = () => {
    if (!editingItemName.trim()) return;
    
    onEditItem(editingItemId, editingItemName.trim());
    setEditingItemId(null);
    setEditingItemName('');
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingItemName('');
  };

  const handleDeleteItem = (itemId) => {
    if (window.confirm(t('Are you sure you want to delete this expense item?'))) {
      onDeleteItem(itemId);
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('Manage Expense Categories')}
      size="xl"
    >
      <div className="p-4 sm:p-6">
        {/* Header Description */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm text-blue-800">
            {t('Create and manage expense categories to streamline your expense tracking. These categories will appear as quick options when adding new expenses.')}
          </p>
        </div>

        {/* Add New Item Section */}
        <div className="mb-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              {t('Add New Category')}
            </h3>
            
            <div className="bg-gray-50 p-4 rounded-xl">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Category Name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder={t('e.g., Office Supplies, Utilities, Raw Materials')}
                    className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                    onKeyPress={(e) => handleKeyPress(e, handleAddItem)}
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={handleAddItem}
                    disabled={!newItemName.trim()}
                    className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <Plus className="h-5 w-5" />
                    <span className="hidden sm:inline">{t('Add Category')}</span>
                    <span className="sm:hidden">{t('Add')}</span>
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t('Create categories that match your business expense types for easier tracking')}
              </p>
            </div>
          </div>
        </div>

        {/* Items List Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              {t('Existing Categories')}
            </h3>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
              {expenseItems.length} {expenseItems.length === 1 ? 'category' : 'categories'}
            </span>
          </div>
          
          {expenseItems.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
              <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-900 mb-2">{t('No Categories Yet')}</h3>
              <p className="text-gray-500 mb-4 max-w-sm mx-auto">
                {t('Start by adding your first expense category above. This will help you organize and track your business expenses more efficiently.')}
              </p>
              <div className="text-sm text-gray-400">
                {t('💡 Popular categories: Office Supplies, Utilities, Marketing, Travel')}
              </div>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
              {expenseItems.map((item) => (
                <div
                  key={item._id}
                  className="group flex items-center space-x-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-md transition-all duration-200"
                >
                  {editingItemId === item._id ? (
                    <>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={editingItemName}
                          onChange={(e) => setEditingItemName(e.target.value)}
                          className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          onKeyPress={(e) => handleKeyPress(e, handleSaveEdit)}
                          autoFocus
                        />
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSaveEdit}
                          className="p-2.5 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                          title={t('Save changes')}
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title={t('Cancel editing')}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 flex items-center space-x-3">
                        {/* The same dot the Expenses page groups by, so a
                            category's colour can be checked here */}
                        <span
                          className={`h-3.5 w-3.5 rounded-full flex-shrink-0 ${categoryColor(item.name).dot}`}
                          aria-hidden="true"
                        />
                        <span className="text-gray-900 font-medium text-base">{item.name}</span>
                      </div>
                      <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEditItem(item)}
                          className="p-2.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title={t('Edit category')}
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteItem(item._id)}
                          className="p-2.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title={t('Delete category')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium transition-all duration-200 border border-gray-200 hover:border-gray-300"
            >
              {t('Done')}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-3 text-center">
            {t('Changes are saved automatically. Categories will be available when adding new expenses.')}
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default ExpenseItemsModal;